import { ProfileHeader, DetailCard, DetailField, SummaryStat } from "../../components/ui/ProfileDetails";
import CompactReference from "../../components/ui/CompactReference";
import { formatMoney } from "../../utils/formatMoney";
import Button from "../../components/ui/Button";
import EnumBadge from "../../components/ui/EnumBadge";
import { confirmAction } from "../../components/ui/confirmAction";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./CustomerDetailsPage.css";
import { getCustomerById, toggleCustomerStatus } from "../../services/customerService";
import { useAccess } from "../../hooks/useAccess";
import { getApiErrorMessage } from "../../utils/apiError";
import CustomerHistory from "./CustomerHistory";
// Import both Customer and the new CustomerShipment type
import { Customer } from "../../types/customer";

// Import Icons
import { FiArrowLeft, FiUser, FiMail, FiGift, FiClock, FiArrowUpRight, FiCreditCard, FiPackage } from "react-icons/fi";
import { getCustomerDisplayName } from "../../utils/customerDisplay";

type DetailTab = "personal" | "wallet" | "shipments" | "referral";

const CustomerDetailsPage: React.FC = () => {
  const { can } = useAccess();
  const [changingStatus, setChangingStatus] = useState(false);
  const [actionError, setActionError] = useState("");
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>("personal");

  useEffect(() => {
    if (!customerId) {
      setError("No Customer ID provided in URL.");
      setLoading(false);
      return;
    }

    const fetchCustomerDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getCustomerById(customerId);
        setCustomer(response.customer);
      } catch (err) {
        setError("Failed to fetch customer details.");

      } finally {
        setLoading(false);
      }
    };

    fetchCustomerDetails();
  }, [customerId]);

  if (loading)
    return <div className="page-loading">Loading customer details...</div>;
  if (error) return <div className="page-error">{error}</div>;
  if (!customer) return <div className="page-error">Customer not found.</div>;

  const fullName = getCustomerDisplayName(customer);

  const renderTabContent = () => {
    switch (activeTab) {
      case "personal":
        return <PersonalDetailsTab customer={customer} />;
      case "wallet":
        return <WalletBalanceTab customer={customer} />;
      case "shipments":
        // Pass the shipments array to the ShipmentsTab component
        return <CustomerHistory key="shipments" customerId={customer.id} preview={customer.relatedCollections?.shipments} />;
      case "referral":
        return <><ReferralTab customer={customer} /><CustomerHistory key="referrals" customerId={customer.id} initialKind="referral-earnings" /></>;
      default:
        return null;
    }
  };

  return (
    <div className="customer-details-page">
      <div className="details-header">
        <div className="breadcrumb">
          <Button variant="text" aria-label="Back to customers" onClick={() => navigate("/customers")}><FiArrowLeft size={18} /></Button>
          <span className="breadcrumb-text">
            All customers <span className="separator">/</span> {fullName}
          </span>
        </div>
      </div>
      <ProfileHeader name={fullName} image={customer.profilePicture}
        subtitle={<>{customer.email} · Customer profile</>}
        badge={<EnumBadge value={customer.isActive ? "active" : "blocked"} />}
        actions={can("customers.manage") ? <Button variant={customer.isActive ? "danger" : "primary"} disabled={changingStatus} onClick={async () => {
          if (!await confirmAction(`${customer.isActive ? "Block" : "Unblock"} this customer?`)) return;
          setChangingStatus(true); setActionError("");
          try { const result = await toggleCustomerStatus(customer.id); setCustomer(previous => previous?.id === result.user.id ? { ...previous, isActive: result.user.isActive } : previous); }
          catch (err) { setActionError(getApiErrorMessage(err) + " Refresh the profile before retrying if the result is uncertain."); }
          finally { setChangingStatus(false); }
        }}>{changingStatus ? "Updating..." : customer.isActive ? "Block customer" : "Unblock customer"}</Button> : undefined} />
      {actionError && <p className="integration-error" role="alert">{actionError}</p>}
      <div className="navo-profile-tabs" aria-label="Customer profile sections">
        <button
          aria-pressed={activeTab === "personal"} className={activeTab === "personal" ? "active" : ""}
          onClick={() => setActiveTab("personal")}
        >
          <FiUser aria-hidden="true" />Personal Details
        </button>
        <button
          aria-pressed={activeTab === "wallet"} className={activeTab === "wallet" ? "active" : ""}
          onClick={() => setActiveTab("wallet")}
        >
          <FiCreditCard aria-hidden="true" />Wallet Balance
        </button>
        <button
          aria-pressed={activeTab === "shipments"} className={activeTab === "shipments" ? "active" : ""}
          onClick={() => setActiveTab("shipments")}
        >
          <FiPackage aria-hidden="true" />Shipments
        </button>
        <button
          aria-pressed={activeTab === "referral"} className={activeTab === "referral" ? "active" : ""}
          onClick={() => setActiveTab("referral")}
        >
          <FiGift aria-hidden="true" />Referral
        </button>
      </div>
      <div className="tab-content">{renderTabContent()}</div>
    </div>
  );
};

// --- SUB-COMPONENTS ---

const PersonalDetailsTab: React.FC<{ customer: Customer }> = ({ customer }) => (
  <div className="navo-detail-grid">
    <DetailCard title="Personal information" description="Account identity and verification" icon={<FiUser />}>
      <dl className="navo-detail-fields">
        <DetailField label="Account name">{customer.accountName?.trim() || "Not provided"}</DetailField>
        <DetailField label="First name">{customer.firstName?.trim() || "Not provided"}</DetailField>
        <DetailField label="Last name">{customer.lastName?.trim() || "Not provided"}</DetailField>
        <DetailField label="Gender">{customer.gender?.trim() || "Not provided"}</DetailField>
        <DetailField label="Country of residence">{customer.countryOfResidence?.trim() || "Not provided"}</DetailField>
        <DetailField label="KYC verification"><EnumBadge value={customer.isKycVerified ? "completed" : "pending"} label={customer.isKycVerified ? "Verified" : "Not verified"} /></DetailField>
      </dl>
    </DetailCard>
    <DetailCard title="Contact details" description="Customer contact information" icon={<FiMail />}>
      <dl className="navo-detail-fields">
        <DetailField label="Email address" wide>{customer.email || "Not provided"}</DetailField>
        <DetailField label="Phone number">{customer.phoneNumber || "Not provided"}</DetailField>
        <DetailField label="Joined">{new Date(customer.createdAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</DetailField>
        <DetailField label="Customer reference" wide><CompactReference value={customer.id} /></DetailField>
      </dl>
    </DetailCard>
  </div>
);

const WalletBalanceTab: React.FC<{ customer: Customer }> = ({ customer }) => (
  <div className="customer-wallet-summary navo-summary-grid">
    <SummaryStat label="₦ wallet" value={formatMoney(customer.wallet?.walletBalanceNGN, "NGN")} hint="Available balance in Nigerian naira" icon={<FiCreditCard />} tone="orange" />
    <SummaryStat label="£ wallet" value={formatMoney(customer.wallet?.walletBalanceGBP, "GBP")} hint="Available balance in British pounds" icon={<FiCreditCard />} />
  </div>
);

const ReferralTab: React.FC<{ customer: Customer }> = ({ customer }) => {
  const wallet = customer.referralWallet;
  const points = (value: number | undefined) => value == null ? "—" : Number(value).toLocaleString();
  return <section className="customer-referral-overview">
    <div className="customer-section-heading"><h2>Referral overview</h2><p>Points earned, redeemed and expired for this customer.</p></div>
    <div className="navo-summary-grid">
      <SummaryStat label="Referral balance" value={points(wallet?.referralBalance)} hint={wallet ? "Points currently available" : "No referral wallet recorded"} icon={<FiGift />} tone="orange" />
      <SummaryStat label="Points used" value={points(wallet?.pointsUsed)} hint="Points redeemed by this customer" icon={<FiArrowUpRight />} />
      <SummaryStat label="Points expired" value={points(wallet?.pointsExpired)} hint="Points past their expiry date" icon={<FiClock />} tone="slate" />
    </div>
  </section>;
};

export default CustomerDetailsPage;
